import React, {Fragment, useCallback, useMemo, useRef} from 'react';
import {Link, Form, Group, Submit, Textarea, Edit, Date, Card, List, Button, Separator, Suggestions, Goals, Breadcrumbs, Layout, Icon} from 'components';
import {last, moment, useParams, useSearchParams, useSelector, redirect, modal, debounce, useFormRef, memoizeOne, isEqual, useField} from 'helpers';
import {goals, calendar, units} from 'store';
import {AnyObject, Goal} from 'types';
import {TemplateModal} from './template-modal';
import {SmartGoalModal} from './smart-goal-modal';

export function EditForm() {
  const params: Record<string, string> = {
    category: 'other',
    ...useParams(),
    ...useSearchParams(),
  }

  const form = useFormRef<Partial<Goal>>();
  const {goal, images} = useSelector((state) => goals.details(state, {id: params.id, context: 'goals'}));
  const examples = useSelector(goals.examples)[params.category ?? ''];
  const findTemplate = useSelector(goals.findTemplate);
  const unit = useSelector(units.getById)(goal?.unitId);
  const {today} = useSelector(calendar.days);

  const initialValues = {
    category: params.category,
    typeId: params.project_id || '0',
    starting_date: today.unix(),
    ...goal,
    unit: unit?.name
  }

  const save = (goal: Partial<Goal>) => {
    const {goal: {id}} = goals.dispatch.upsert({
      ...goal,
      unitId: units.dispatch.upsert(goal.unit).unit?.serverId ?? 0
    });
    if (goal.id) {
      redirect('../');
    } else {
      redirect(`/goals/${id}/edit/`, true);
      const template = findTemplate(goal.name);
      if (goal.isSmart) {
        redirect('../review/');
      } else if (template) {
        modal(TemplateModal, {id, ...template})();
      } else {
        redirect(`../milestones/?redirect=${encodeURIComponent(params.redirect ?? '')}`);
      }
    }
  }

  const onChange = (values: Partial<Goal>, diff: AnyObject) => {
    if (values.isSmart && isEqual(diff, {name: ''})) {
      form.current?.setValues(values => ({
        ...values,
        isSmart: 0
      }));
    }
  }

  const NameSuffix = useCallback(({value}: {value?: string}) => {
    const [{value: isSmart}] = useField('isSmart');
    if (!goal && value?.trim() && !isSmart) {
      return (
        <Button
          className={['tiny', 'orange', 'appear']}
          children="Ask ChatGPT"
          onPointerDown={modal(SmartGoalModal, {
            name: value,
            dismissible: false,
            onSuccess: ({goal}) => {
              form.current?.setValues(values => ({
                ...values,
                ...goal,
                isSmart: 1
              }));
            }
          })}
        />
      );
    }
    return <Button className={['tiny', 'isHidden']}>_</Button>;
  }, []);

  const NameSuggestions = useMemo(() => {
    if (examples?.length) {
      return (props: any) => (
        <Suggestions.List {...props}>
          {examples.map(example =>
            <p key={example.id}>{example.name}</p>
          )}
        </Suggestions.List>
      );
    }
  }, [examples]);

  return (
    <Form initialValues={initialValues} onSubmit={save} delay innerRef={form} onChange={onChange}>
      {({values}) => (
        <Fragment>
          <Layout.Header justified>
            <Breadcrumbs>
              <Link to="/goals/">Goals</Link>
              <b>Set Goal</b>
            </Breadcrumbs>
            { goal &&
              <Button.Back className={['pull-right', 'small', 'borderless']} />
            }
            <Submit className="small" icon={!goal && 'arrow-right'}>
              { goal ? [ 'Save', 'Saving..', 'Saved' ] : [values.isSmart ? 'Create plan' : 'Next']}
            </Submit>
          </Layout.Header>
          <div>
            <Card background={last(images) || values.category} is-tall>
              <Group>
                <Edit
                  bold
                  name="name"
                  placeholder={NameSuggestions ? 'Enter goal name or select from the list..' : 'Enter goal name..'}
                  required
                  Suggestions={NameSuggestions}
                  Suffix={NameSuffix}
                />
                <Edit
                  name="note"
                  placeholder="Enter note.."
                />
              </Group>
            </Card>
            <List small-gutter>
              <Date name="target_date" label="Target Date" minDate={values.starting_date} />
              <Date name="starting_date" label="Starting Date" maxDate={values.target_date} value={moment().startOf('day').unix()} required />
              <Edit type="number" name="target_value" label="Numerical Target" Suffix={Goals.NumberUnit} unitName="unit" />
              <Edit type="number" name="starting_value" label="Starting Value" Suffix={Goals.NumberUnit} unitName="unit" />
              <Goals.Single.CategoryDropdown />
              <Separator className="large" heading="Dig Deeper" />
              <p>
                <b className="label">Why is this goal so important or significant to you?</b>
                <Textarea single name="importance" />
              </p>
              <p>
                <b className="label">What would you gain by achieving this goal?</b>
                <Textarea single name="gain" />
              </p>
              <p>
                <b className="label">What’s the price you are willing to pay to achieve this goal?</b>
                <Textarea single name="price" />
              </p>
              <p>
                <b className="label">What risks would you take while pursuing this goal?</b>
                <Textarea single name="risks" />
              </p>
              <p>
                <b className="label">What are possible obstacles?</b>
                <Textarea single name="obstacles" />
              </p>
              <p>
                <b className="label">Why didn’t you achieve this goal already? What’s stopping you?</b>
                <Textarea single name="holdingback" />
              </p>
              <p>
                <b className="label">What can you do differently now?</b>
                <Textarea single name="dodifferently" />
              </p>
            </List>
          </div>
        </Fragment>
      )}
    </Form>
  );
}



